import {useEffect, useMemo, useState} from 'react'
import {createPortal} from 'react-dom'
import {useNavigate, useParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import * as turf from '@turf/turf'

import {openAlert} from '../../../../../../../_metronic/elements/NotificationAlert'
import {KTSVG} from '../../../../../../../_metronic/helpers'
import {useErrorHandler} from '../../../../../../../_metronic/helpers/ErrorHandler'
import {useViewFieldContext} from '../../ViewFieldContext'
import {DrawTools} from '../../../../Map/Draw/DrawState'
import {mapState} from '../../../../Map/MapState'
import {useMapLayersV2} from '../../../../Map/MapLayersV2Context'
import {useDrawV2} from '../../../../Map/Draw/DrawV2Context'
import DrawControl from '../../../../Map/Draw/DrawControl'
import * as GapService from '../../../services/GapService'
import {formatHectares} from '../../../../../../shared/numberFormat'
import '../../../CreateFlow.style.scss'

const buildFeatureCount = (getAll) => {
  const features = getAll()
  return {
    features,
    count: features.features.length,
  }
}

const getFeatureArea = (feature) => {
  if (!feature?.geometry) {
    return 0
  }

  return turf.area(feature.geometry) / 10000
}

const MapDrawingGuide = ({visible, mode, pointCount}) => {
  const {t} = useTranslation()
  const message = useMemo(() => {
    if (mode === DrawTools.CIRCLE) {
      return t('gaps:create.draw.guide_circle')
    }

    if (pointCount < 1) {
      return t('gaps:create.draw.guide_first')
    }

    if (pointCount < 3) {
      return t('gaps:create.draw.guide_continue')
    }

    return t('gaps:create.draw.guide_finish')
  }, [mode, pointCount, t])

  if (!visible || typeof document === 'undefined') {
    return null
  }

  return createPortal(
    <div className='gis-map-guidance'>
      <div className='gis-map-guidance__card'>{message}</div>
    </div>,
    document.body
  )
}

const GapDraftCard = ({feature, index, onFocus, onRemove}) => {
  const {t} = useTranslation()
  const area = getFeatureArea(feature)
  const displayIndex = index + 1

  return (
    <div className='gis-create-flow-draft gis-create-flow-draft--readonly'>
      <button
        type='button'
        className='btn btn-color-muted btn-active-color-primary btn-sm field-view-card__focus gis-create-flow-draft__icon-button gis-create-flow-draft__focus-button'
        onClick={() => onFocus(feature)}
        title={t('gaps:create.buttons.focus')}
        aria-label={t('gaps:create.buttons.focus')}
      >
        <KTSVG
          path='/media/icons/duotune/maps/map007.svg'
          className='svg-icon svg-icon-1 field-view-card__focus-icon'
        />
      </button>

      <div className='gis-create-flow-draft__field gis-create-flow-draft__readonly-field'>
        <p className='gis-create-flow-draft__label'>
          {t('gaps:create.draft.name', {index: displayIndex})}
          <span>{formatHectares(area)}</span>
        </p>
      </div>

      <button
        type='button'
        className='btn btn-light-danger gis-create-flow-draft__icon-button'
        onClick={() => onRemove(feature.id)}
        title={t('gaps:create.buttons.remove')}
        aria-label={t('gaps:create.buttons.remove')}
      >
        <KTSVG path='/media/icons/duotune/art/art004.svg' className='svg-icon svg-icon-2' />
      </button>
    </div>
  )
}

const CreateGap = () => {
  const [loading, setLoading] = useState(false)
  const [zoneDrafts, setZoneDrafts] = useState([])
  const [drawingPointCount, setDrawingPointCount] = useState(0)
  const {errorHandler} = useErrorHandler()
  const {field_id} = useParams()
  const {load} = useViewFieldContext()
  const {t} = useTranslation()
  const navigate = useNavigate()
  const drawV2 = useDrawV2()
  const {startDrawing, endDrawing, deleteAllLayers, deleteLayer, getAll, currentMode} = drawV2
  const mapLayersV2 = useMapLayersV2()
  const gapToolLabels = useMemo(
    () => ({
      [DrawTools.POLYGON]: t('gaps:create.draw.polygon'),
      [DrawTools.CIRCLE]: t('gaps:create.draw.circle'),
    }),
    [t]
  )
  const featureCount = zoneDrafts.length
  const totalDraftHa = useMemo(() => {
    return zoneDrafts.reduce((total, feature) => total + getFeatureArea(feature), 0)
  }, [zoneDrafts])

  const syncDrawFeatures = () => {
    const nextState = buildFeatureCount(getAll)
    setZoneDrafts(nextState.features.features)
    return nextState
  }

  useEffect(() => {
    document.getElementById('kt_content_container')?.scroll({top: 0, behavior: 'smooth'})
    mapLayersV2.land.unfillByFieldId(field_id)
  }, [field_id, mapLayersV2.land])

  useEffect(() => {
    startDrawing({
      tools: [DrawTools.POLYGON, DrawTools.CIRCLE],
      keepModeAfterCreate: true,
      onCreate: () => {
        setDrawingPointCount(0)
        syncDrawFeatures()
      },
      onDelete: () => {
        setDrawingPointCount(0)
        syncDrawFeatures()
      },
      onUpdate: () => {
        syncDrawFeatures()
      },
    })

    return () => {
      endDrawing()
      deleteAllLayers()
    }
  }, [deleteAllLayers, endDrawing, getAll, startDrawing])

  useEffect(() => {
    setDrawingPointCount(0)

    if (currentMode !== DrawTools.POLYGON) {
      return
    }

    let disposed = false
    let timerId = null
    let container = null

    const onCanvasClick = () => {
      setDrawingPointCount((previousCount) => previousCount + 1)
    }

    const bindCanvasClicks = () => {
      if (disposed) {
        return
      }

      const nextContainer = mapState?.map?.getCanvasContainer?.()

      if (!(nextContainer instanceof HTMLElement)) {
        timerId = window.setTimeout(bindCanvasClicks, 120)
        return
      }

      container = nextContainer
      container.addEventListener('click', onCanvasClick)
    }

    bindCanvasClicks()

    return () => {
      disposed = true

      if (timerId != null) {
        window.clearTimeout(timerId)
      }

      if (container instanceof HTMLElement) {
        container.removeEventListener('click', onCanvasClick)
      }
    }
  }, [currentMode])

  useEffect(() => {
    return () => {
      mapLayersV2.land.fillByFieldId(field_id)
    }
  }, [field_id, mapLayersV2.land])

  const fitZone = (feature) => {
    if (!feature?.geometry) {
      return
    }

    mapLayersV2.fitToPolygon(feature.geometry)
  }

  const removeZone = (featureId) => {
    deleteLayer(featureId)
    setZoneDrafts((prev) => prev.filter((feature) => feature.id !== featureId))
  }

  const save = async () => {
    const {features, count} = buildFeatureCount(getAll)

    if (count < 1) {
      openAlert(
        'error',
        t('gaps:create.errors.polygon_required.title'),
        t('gaps:create.errors.polygon_required.message')
      )
      return
    }

    setLoading(true)
    const res = await errorHandler(
      GapService.create(field_id, {polygons: features.features.map((feature) => feature.geometry)})
    )

    if (res?.ok) {
      const message =
        count === 1
          ? t('gaps:create.alerts.success.singular')
          : t('gaps:create.alerts.success.plural')
      openAlert('success', t('gaps:create.alerts.success.title'), message)
      navigate(`/gis/field/view/${field_id}`)

      mapLayersV2.reloadSource()
      load()
    }

    setLoading(false)
  }

  return (
    <>
      <MapDrawingGuide
        visible={currentMode === DrawTools.POLYGON || currentMode === DrawTools.CIRCLE}
        mode={currentMode}
        pointCount={drawingPointCount}
      />
      <div className='d-flex flex-column flex-row-auto field-view-flex-container'>
        <div className='d-flex flex-column-auto displayable mb-4'>
          <div className='card shadow-sm field-view-container right-card-data gis-create-flow-card gis-create-flow-card--guided'>
          <div className='card-header'>
            <h4 className='gis-create-flow-title'>{t('gaps:create.title')}</h4>
          </div>

          <div className='card-body'>
            <div className='gis-create-flow-action-bar'>
              <DrawControl
                className='map-draw-control--wizard map-draw-control--two-tools'
                showLabel={false}
                toolLabels={gapToolLabels}
              />
            </div>

            {featureCount > 0 ? (
              <>
                <div className='gis-create-flow-review-bar'>
                  <span>
                    {t(
                      featureCount === 1
                        ? 'gaps:create.draft.count_one'
                        : 'gaps:create.draft.count_other',
                      {count: featureCount}
                    )}
                  </span>
                  <strong>{formatHectares(totalDraftHa)}</strong>
                </div>

                <div className='gis-create-flow-drafts'>
                  {zoneDrafts.map((feature, index) => (
                    <GapDraftCard
                      key={feature.id}
                      feature={feature}
                      index={index}
                      onFocus={fitZone}
                      onRemove={removeZone}
                    />
                  ))}
                </div>
              </>
            ) : null}
          </div>

          <div className='card-footer'>
            <button
              type='button'
              className='btn btn-light gis-create-flow-footer-button'
              onClick={() => navigate(`/gis/field/view/${field_id}`)}
              disabled={loading}
            >
              {t('gaps:create.buttons.cancel')}
            </button>
            <button
              type='button'
              onClick={save}
              className='btn btn-primary gis-create-flow-footer-button'
              disabled={featureCount < 1 || loading}
            >
              {!loading && t('gaps:create.buttons.create')}
              {loading && (
                <span className='indicator-progress' style={{display: 'block'}}>
                  {t('gaps:create.buttons.creating')}{' '}
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
              )}
            </button>
          </div>
        </div>
        </div>
      </div>
    </>
  )
}

export default CreateGap
